import styled from "styled-components";

import { QUERIES, SPACING } from "../../../../../utils/constants";
import { LiabilityPaymentStatus } from "../../../types";
import Button, { BaseButton } from "../../../../../components/Button";

const BAND_TINT = "var(--color-tab-background)";

const STATUS_TEXT: Record<LiabilityPaymentStatus, string> = {
  [LiabilityPaymentStatus.SCHEDULED]: "var(--color-success)",
  [LiabilityPaymentStatus.OVERPAYMENT]: "var(--color-success)",
  [LiabilityPaymentStatus.LATE]: "var(--color-warning)",
};

const STATUS_BG: Record<LiabilityPaymentStatus, string> = {
  [LiabilityPaymentStatus.SCHEDULED]: "var(--color-success-background)",
  [LiabilityPaymentStatus.OVERPAYMENT]: "var(--color-success-background)",
  [LiabilityPaymentStatus.LATE]: "var(--color-warning-background)",
};

const STATUS_BORDER: Record<LiabilityPaymentStatus, string> = {
  [LiabilityPaymentStatus.SCHEDULED]: "var(--color-success-border)",
  [LiabilityPaymentStatus.OVERPAYMENT]: "var(--color-success-border)",
  [LiabilityPaymentStatus.LATE]: "var(--color-warning-border)",
};

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);
  overflow: hidden;
`;

export const HeaderTitle = styled.h2`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: var(--color-text-default);
  margin: 0;
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const DesktopHeader = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${SPACING.lg}px;
    flex-wrap: wrap;
    padding: ${SPACING.lg}px;
    background: ${BAND_TINT};
    border-bottom: 1px solid var(--color-border);
  }
`;

export const HeaderMeta = styled.span`
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

const ROW_GRID = `
  grid-template-columns:
    minmax(92px, 1fr)
    minmax(96px, 0.9fr)
    minmax(80px, 0.9fr)
    minmax(90px, 0.9fr)
    minmax(80px, 0.9fr)
    minmax(110px, 1.2fr)
    36px;
`;

export const Table = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: block;
  }
`;

export const TableHead = styled.div`
  display: grid;
  ${ROW_GRID}
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;
  background: var(--color-gray-background);
  border-bottom: 1px solid var(--color-border);
`;

export const TableHeadCell = styled.span<{ $align?: "left" | "right" }>`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
  text-align: ${({ $align }) => $align ?? "left"};
`;

export const TableRow = styled.div`
  display: grid;
  ${ROW_GRID}
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const DateValue = styled.span`
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);
`;

export const TotalValue = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  text-align: right;
  color: var(--color-text-default);
`;

export const PrincipalValue = styled.span`
  font-size: var(--text-base);
  font-weight: 600;
  text-align: right;
  color: var(--color-primary);
`;

export const InterestValue = styled.span`
  font-size: var(--text-base);
  text-align: right;
  color: var(--color-text-secondary);
`;

export const BalanceValue = styled.span`
  font-size: var(--text-base);
  text-align: right;
  color: var(--color-text-secondary);
`;

export const MenuCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const StatusPill = styled.span<{ $status: LiabilityPaymentStatus }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  justify-self: start;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $status }) => STATUS_TEXT[$status]};
  background: ${({ $status }) => STATUS_BG[$status]};
  border: 1px solid ${({ $status }) => STATUS_BORDER[$status]};
`;

export const MobileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  background: ${BAND_TINT};
  border-bottom: 1px solid var(--color-border);

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const MobileHeaderLeft = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  min-width: 0;
`;

export const CollapseChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  color: var(--color-text-secondary);
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "-90deg")});

  svg {
    display: block;
  }
`;

export const CountPill = styled.span`
  flex-shrink: 0;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 500;
  white-space: nowrap;
  color: var(--color-text-secondary);
  background: var(--color-card-background);
  border: 1px solid var(--color-border);
`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const PaymentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardDateWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING.md}px;
  min-width: 0;
`;

export const CardDate = styled.span`
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);
`;

export const CardAmountRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardTotal = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--color-text-default);
`;

export const CardBalance = styled.span`
  font-size: var(--text-base);
  text-align: right;
  color: var(--color-text);
`;

export const CardSplit = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);

  strong {
    font-weight: 600;
    color: var(--color-primary);
  }
`;

export const CardNote = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-muted);
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  background: ${BAND_TINT};
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING.lg}px;
  }
`;

export const FooterNote = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
  min-width: 0;
`;

export const LoadMoreButton = styled(Button)`
  flex-shrink: 0;
`;
